import {
  AnalyticsGranularity,
  ClicksBaseFilters,
  ClicksBreakdown,
  ClicksTrendingMetric,
} from "../../api/analyticsAdmin";
import {
  clicksBusinessFunnel,
  clicksOverview,
  clicksSeries,
  clicksTrending,
} from "../../api/analyticsStream";
import { ClicksFilters } from "./ClicksFilterBar";

/**
 * The six breakdowns the tab renders under the headline funnel, each its own
 * section so one failing does not blank the rest.
 */
export const CLICKS_BREAKDOWNS = [
  { id: "bdSource", key: "Source", label: "Source" },
  { id: "bdSection", key: "Section", label: "Section" },
  { id: "bdCategory", key: "Category", label: "Category" },
  { id: "bdOfferType", key: "OfferType", label: "Offer type" },
  { id: "bdGender", key: "Gender", label: "Gender" },
  { id: "bdAgeBand", key: "AgeBand", label: "Age band" },
] as const satisfies readonly {
  id: string;
  key: ClicksBreakdown;
  label: string;
}[];

// Every business active in the window (the API ceiling is 2000; production has
// ~662). The lookup and the client-side sorts only tell the truth over the
// complete set — and this must stay equal to BusinessFunnelTopN in the backend
// warm-up, or the page misses the hourly-warmed cache entry.
export const BUSINESS_FUNNEL_TOP_N = 2000;

// from/to travel only with a custom range; for the fixed presets the server
// resolves the dates itself, and sending stale ones would change the cache key.
const base = (filters: ClicksFilters): ClicksBaseFilters => ({
  preset: filters.preset,
  ...(filters.preset === "Custom" ? { from: filters.from, to: filters.to } : {}),
  platform: filters.platform,
  audience: filters.audience,
  conversionWindowDays: filters.conversionWindowDays,
});

export interface ClicksSectionInputs {
  filters: ClicksFilters;
  trendGranularity: AnalyticsGranularity;
  trendingGranularity: AnalyticsGranularity;
  trendingTopN: number;
  trendingMetric: ClicksTrendingMetric;
}

/**
 * Everything the Clicks tab asks for, as one batch.
 *
 * Keys are the section ids, so a typo is a compile error rather than a section
 * that silently never arrives. The hook re-requests only the sections whose query
 * actually changed, which is why the widget controls above can live on the page
 * without every nudge costing a full rebuild.
 */
export const clicksSections = (inputs: ClicksSectionInputs) => {
  const shared = base(inputs.filters);

  return {
    series: clicksSeries({
      ...shared,
      breakdown: "None",
      granularity: inputs.trendGranularity,
    }),
    overview: clicksOverview({ ...shared, breakdown: "None" }),
    bdSource: clicksOverview({ ...shared, breakdown: "Source" }),
    bdSection: clicksOverview({ ...shared, breakdown: "Section" }),
    bdCategory: clicksOverview({ ...shared, breakdown: "Category" }),
    bdOfferType: clicksOverview({ ...shared, breakdown: "OfferType" }),
    bdGender: clicksOverview({ ...shared, breakdown: "Gender" }),
    bdAgeBand: clicksOverview({ ...shared, breakdown: "AgeBand" }),
    businessFunnel: clicksBusinessFunnel({
      ...shared,
      topN: BUSINESS_FUNNEL_TOP_N,
      // Always the same request, whatever the dropdowns say: sorting and the
      // small-sample cut happen on the client, off a payload that already carries
      // every field they need. One shape is one cache entry, and the server's is
      // the query that outlives the gateway timeout.
      minOpens: 1,
      sort: "Volume",
    }),
    trending: clicksTrending({
      ...shared,
      granularity: inputs.trendingGranularity,
      topN: inputs.trendingTopN,
      metric: inputs.trendingMetric,
    }),
  };
};
